import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Row } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import getChartColorsArray from '../../../../../Components/Common/ChartsDynamicColor';
import { useDispatch, useSelector } from 'react-redux';
import { getDataReportTurbidity } from '../../../../../slices/thunks';
import config from '../../../../../config';
import moment from 'moment';
import OPlaceholder from '../../../../../Components/OElements/OPlaceholder';

const TurbiditySummary = ({ title, um, code_siep, h100 = true, height = 150, showHeader = true, showFooter = true }) => {
    const dispatch = useDispatch();
    const [dataInterval, setDataInterval] = useState('24h');
    const [chartCategories, setChartCategories] = useState([]);

    const dataColors = '["--vz-primary", "--vz-secondary"]';
    const revenueExpensesChartsColors = getChartColorsArray(dataColors);

    // Acceder directamente al slice del estado
    const { data, isLoading, isSuccess, isError } = useSelector((state) => state.TurbidityProvider || {});

    useEffect(() => {
        // Llama a la acción para obtener datos
        const requestData = {
            username: config.user_providers.USER,
            password: config.user_providers.PASS,
            service: 'innovex',
            interval: dataInterval,
            siep: code_siep,
            variable: 'turbidity',
        };

        if (code_siep) {
            dispatch(getDataReportTurbidity(requestData));
        }
    }, [dataInterval, code_siep]);


    const seriesData = useMemo(() => {
        if (!data) {
            return [
                { name: '2m', data: [] },
            ];
        }

        return [
            { name: '2m', data: data['2m'] ? Object.values(data['2m']) : [] },
        ];
    }, [data]);


    useEffect(() => {
        if (data?.time) {
            const intervalFormats = {
                '24h': 'HH:00',
                '7d': 'DD-MMM',
                '15d': 'DD-MMM',
                '1m': 'DD-MMM',
                'ciclo': 'DD-MM-YYYY',
            };

            const processData = (timeData, type) => {
                const format = intervalFormats[type] || 'YYYY-MM-DD';
                return Object.values(timeData).map(time => moment(time).format(format));
            };

            setChartCategories(processData(data.time, dataInterval));
        }
    }, [data, dataInterval, code_siep]);


    const calculateStats = data => {
        if (!Array.isArray(data)) return { min: '--', max: '--', avg: '--' };
        const validData = data.filter(val => Number.isFinite(val));
        if (validData.length === 0) return { min: '--', max: '--', avg: '--' };

        const sum = validData.reduce((acc, val) => acc + val, 0);
        return {
            min: Math.min(...validData),
            max: Math.max(...validData),
            avg: (sum / validData.length).toFixed(1),
        };
    };

    const stats = useMemo(() => {
        return seriesData.map(serie => ({
            name: serie.name,
            stats: calculateStats(serie.data),
        }));
    }, [seriesData]);

    const Charts = ({ dataColors, series }) => {
        const safeSeries = JSON.parse(JSON.stringify(series || []));

        const options = {
            chart: {
                height: 200,
                type: 'area',
                toolbar: false,
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: 'smooth',
                width: 1,
            },
            xaxis: {
                categories: chartCategories,
                labels: {
                    rotate: -70,
                    hideOverlappingLabels: true,
                    style: {
                        fontSize: '8px', // Ajusta el tamaño de la letra
                    },
                },
            },
            yaxis: {
                show: true,
                labels: {
                    formatter: function (value) {
                        return `${value.toFixed(0)} ` + um;
                    },
                    style: {
                        fontSize: '8px',
                    },
                },
            },
            colors: revenueExpensesChartsColors,
            fill: {
                opacity: 0.06,
                colors: revenueExpensesChartsColors,
                type: 'solid',
            },
            tooltip: {
                y: {
                    formatter: function (value) {
                        return `${value} ` + um;
                    },
                },
            },
            legend: {
                show: false, // Desactiva la leyenda
            },
        };

        return (
            <ReactApexChart
                options={{
                    ...options,
                    noData: {
                        text: "No hay datos disponibles",
                        align: "center",
                        verticalAlign: "middle",
                        style: {
                            color: "#999",
                            fontSize: "11px",
                        },
                    },
                }}
                series={safeSeries}
                type="area"
                height={height}
                className="apex-charts"
            />
        );
    };

    return (
        <Card style={h100 ? { height: '100%' } : {}}>
            {showHeader && (
            <CardHeader className="p-2 align-items-center d-flex">
                <h5 className="card-title mb-0 flex-grow-1">{title}</h5>
                <div className="btn-box-dashboard">
                    {['24h', '7d', '15d', '1m'].map(type => (
                        <Button
                            key={type}
                            color={dataInterval === type ? 'secondary' : 'primary'}
                            size="sm"
                            className={`btn-soft-secondary ${dataInterval === type ? 'active' : ''}`}
                            onClick={() => setDataInterval(type)}
                        >
                            {type.toUpperCase()}
                        </Button>
                    ))}
                </div>
            </CardHeader>
            )}
            <CardBody className="p-0 m-0">
                <div id="revenue-expenses-charts" dir="ltr" style={{ paddingLeft: '0px', paddingRight: '5px' }}>
                    <Charts series={seriesData} />
                </div>
            </CardBody>
            {showFooter && (
            <CardFooter className='p-0'>
                <Row className="m-2 p-0">
                    {(data?.summary ? Object.entries(data.summary) : []).map(([key, values = {}], index) => (
                        <Col
                            key={index}
                            md={12}
                            className={`m-0 p-0 ${index !== 0 ? 'border-start' : ''}`}
                        >
                            <h5 className="m-0 text-center" style={{ color: revenueExpensesChartsColors[index] }}>
                                {key}
                            </h5>
                            <ul className="list-inline main-chart text-center mb-0">
                                <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                    <h5 className="m-0 fs-11">
                                        {values.min || '--'}
                                    </h5>
                                    <span className="text-muted d-inline-block fs-10 align-middle">Mínimo</span>
                                </li>
                                <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                    <h5 className="m-0 fs-11">
                                        {values.avg || '--'}
                                    </h5>
                                    <span className="text-muted d-inline-block fs-10 align-middle">Promedio</span>
                                </li>
                                <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                    <h5 className="m-0 fs-11">
                                        {values.max || '--'}
                                    </h5>
                                    <span className="text-muted d-inline-block fs-10 align-middle">Máximo</span>
                                </li>
                            </ul>
                        </Col>
                    ))}

                    {/* Fallback calculado desde seriesData cuando no hay summary */}
                    {(!data?.summary || Object.keys(data.summary).length === 0) && (
                        <Col md={12} className="m-0 p-0">
                            <h5 className="m-0 text-center">{stats[0]?.name ?? 'Sin datos'}</h5>
                            <ul className="list-inline main-chart text-center mb-0">
                                <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                    <h5 className="m-0 fs-11">{stats[0]?.stats?.min !== '--' ? `${stats[0].stats.min} ${um}` : '--'}</h5>
                                    <span className="text-muted d-inline-block fs-10 align-middle">Mínimo</span>
                                </li>
                                <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                    <h5 className="m-0 fs-11">{stats[0]?.stats?.avg !== '--' ? `${stats[0].stats.avg} ${um}` : '--'}</h5>
                                    <span className="text-muted d-inline-block fs-10 align-middle">Promedio</span>
                                </li>
                                <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                    <h5 className="m-0 fs-11">{stats[0]?.stats?.max !== '--' ? `${stats[0].stats.max} ${um}` : '--'}</h5>
                                    <span className="text-muted d-inline-block fs-10 align-middle">Máximo</span>
                                </li>
                            </ul>
                        </Col>
                    )}
                </Row>
            </CardFooter>
            )}
        </Card>
    );
};

export default TurbiditySummary;
